import { Fragment, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useQuery } from '@tanstack/react-query'
import bannerMd from 'assets/images/campaign/금융사기_img_884_360.png'
import bannerLg from 'assets/images/campaign/금융사기_img_1920_1200.png'
import mainMd from 'assets/images/campaign/캠페인_main_img_884_360.jpg'
import mainLg from 'assets/images/campaign/캠페인_main_img_1920__1200.jpg'
import footerMd from 'assets/images/donation/footer_img_884_360.png'
import footerLg from 'assets/images/donation/footer_img_1920_1200.png'
import CsrCardComponent from 'features/csr/components/CsrCardComponent'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getCsrCampaign, getCsrKeywords } from 'shared/api/fetchs'
import SeoHelmet from 'shared/components/common/SeoHelmet'
import TopButton from 'shared/components/common/TopButton'
import STYLE_GUIDE from 'shared/constants/color'
import styled from 'styled-components'

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

const Campaign = () => {
  const isMobile = useMediaQuery({ query: '(max-width:1199px)' })
  const [isMore, setIsMore] = useState([])

  const { data: keywordData } = useQuery({
    queryKey: ['csrKeywords', 0],
    queryFn: () => getCsrKeywords(1), // 1 = 캠페인
  })

  const { data: campaignData } = useQuery({
    queryKey: ['csrCampaign', 0],
    queryFn: () => getCsrCampaign(),
  })

  const keywords = keywordData?.Keyword || []
  const campaigns = campaignData?.Campaign || []

  const handleMoreBtn = (idx) => setIsMore([...isMore, idx])

  /* SNAP SCROLL START */
  const headerRef = useRef(null)
  const contentsRef = useRef(null)
  const isScrolling = useRef(false)
  const touchStartY = useRef(0)

  useEffect(() => {
    const snapTo = (target) => {
      isScrolling.current = true
      document.body.style.overflow = 'hidden'
      gsap.to(window, {
        duration: 1,
        scrollTo: target,
        ease: 'power2.out',
        onComplete: () => {
          document.body.style.overflow = ''
          isScrolling.current = false
        },
      })
    }

    const trySnap = (deltaY) => {
      if (isScrolling.current || !headerRef.current || !contentsRef.current) return

      if (Math.ceil(window.scrollY) < contentsRef.current.offsetTop) {
        if (deltaY > 0) snapTo(contentsRef.current.offsetTop)
        if (deltaY < 0) snapTo(headerRef.current.offsetTop)
      }
    }

    // 마우스 휠
    const handleWheel = (e) => {
      if (isScrolling.current) {
        e.preventDefault()
        return
      }
      trySnap(e.deltaY)
    }

    // 터치 시작 (좌표 저장)
    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY
    }

    // 터치 종료 (방향 계산)
    const handleTouchEnd = (e) => {
      const endY = e.changedTouches[0].clientY
      const deltaY = touchStartY.current - endY
      trySnap(deltaY)
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])
  /* SNAP SCROLL END */

  return (
    <>
      <SeoHelmet />
      <CampaignWrap>
        <div
          className="header"
          ref={headerRef}
          style={isMobile ? {} : { backgroundImage: `url(${mainLg})` }}
        >
          <div className="header_text_container container">
            <h1 className="header_title">
              STOP. THINK. CONNECT
              <br />
              잠시 멈추고. 생각한 뒤. 연락하세요.
            </h1>
            <div className="header_subtitle">
              데이터유니버스는 보이스피싱 예방을 위한 서비스 개발 뿐 아니라, <b>‘금융사기 근절’</b>
              을 목표로 다양한 사회 공헌 활동도 활발히 진행하고 있습니다.
              <br />
              <b>
                수상한 문자나 전화를 수신하였다면 STOP 잠시 멈추고, THINK 생각한 뒤, CONNECT
                연락하세요.
              </b>
            </div>
          </div>
          {isMobile && <img className="header_background_img" src={mainMd} alt="배경" />}
        </div>
        <div className="contents container span" ref={contentsRef}>
          <div className="contents_keyword_container">
            <h2 className="contents_title">보이스피싱 {isMobile && <br />}이제 그만!</h2>
            <div className="contents_description">
              보이스피싱은 매년 수법이 교묘해지며, 우리의 일상과 안전을 위협하는 주요 범죄로
              자리잡고 있습니다.
              <br />
              {isMobile ? (
                <b>
                  데이터유니버스는 남녀노소 누구나 당할 수 있는 보이스피싱을 예방하기 위해 캠페인 등
                  다양한 활동을 통해 금융사기 근절에 기여하고 있습니다.
                </b>
              ) : (
                <>
                  데이터유니버스는 남녀노소 누구나 당할 수 있는 보이스피싱을 예방하기 위해 캠페인 등
                  다양한 활동을 통해 금융사기 근절에 기여하고 있습니다.
                </>
              )}
            </div>
            <div className="keyword_chips_list">
              {keywords?.map((keyword, index) => (
                <div className="keyword_chip_item" key={index}>
                  {keyword}
                </div>
              ))}
            </div>
          </div>
          <div className="contents_card_list">
            {campaigns.map((item, index) => (
              <Fragment key={item?.Idx}>
                <CsrCardComponent
                  data={item}
                  isMoreOpen={isMore.includes(item?.Idx)}
                  handleMoreBtn={handleMoreBtn}
                />
                {!!campaigns?.[index + 1] && <div className="card_divider" />}
              </Fragment>
            ))}
          </div>
        </div>
        <div
          className="banner"
          style={{ backgroundImage: `url(${isMobile ? bannerMd : bannerLg})` }}
        >
          <div className="container">
            <h2 className="banner_title">금융사기 없는 {isMobile && <br />}세상을 위해</h2>
            <div className="banner_description">
              데이터유니버스는 보이스피싱을 비롯한 각종 금융사기 근절을 위한 캠페인을 지속적으로
              진행할 예정입니다.
              <br />
              금융사기는 단순히 예방 서비스 제공을 넘어 우리 모두가 함께 해결해 나가야 할 사회
              문제인 만큼,
              <br />
              안전한 세상을 위해 앞으로도 끊임없이 노력하고 기여하겠습니다.
            </div>
          </div>
        </div>
        <div
          className="footer"
          style={{ backgroundImage: `url(${isMobile ? footerMd : footerLg})` }}
        >
          <span>
            <span style={{ color: STYLE_GUIDE.COLOR.main1 }}>데이터유니버스</span>
            {isMobile
              ? '는 우리 사회 구성원\n모두가 더 나은 세상에서 살아갈 수 있도록\n앞으로도 함께하겠습니다.'
              : '는 우리 사회 구성원 모두가\n더 나은 세상에서 살아갈 수 있도록 앞으로도 함께하겠습니다.'}
          </span>
        </div>
        <TopButton />
      </CampaignWrap>
    </>
  )
}

export default Campaign

const CampaignWrap = styled.div`
  b {
    font-weight: 700;
  }

  .header {
    width: 100%;
    height: min(calc(100vw / 16 * 9), 1080px);
    background-size: auto min(calc(100vw * 535 / 1920), 535px);
    background-position: center bottom;
    background-repeat: no-repeat;

    @media screen and (min-width: 1920px) {
      background-size: 100% auto;
    }

    @media screen and (max-width: 1199px) {
      height: auto;
    }
  }

  .header_text_container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-top: 220px;

    @media screen and (max-width: 1199px) {
      padding-top: 120px;
    }
  }

  .header_title {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 56px;
    font-weight: 700;
    line-height: 72px;

    @media screen and (max-width: 1199px) {
      font-size: 36px;
      line-height: 48px;
    }
  }

  .header_subtitle {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 20px;
    font-weight: 400;
    line-height: 32px;

    @media screen and (max-width: 1199px) {
      font-size: 16px;
      line-height: 30px;
    }
  }

  .header_background_img {
    width: 100vw;

    @media screen and (max-width: 1199px) {
      margin-top: 189px;
    }

    @media screen and (max-width: 883px) {
      margin-top: 48px;
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    gap: 120px;
    padding: 120px 0;

    @media screen and (max-width: 1199px) {
      gap: 80px;
      padding: 80px 0;
    }
  }

  .contents_keyword_container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 50px;
    font-size: 18px;
    font-weight: 400;
    line-height: 30px;
    text-align: center;

    @media screen and (max-width: 1199px) {
      align-items: flex-start;
      gap: 30px;
      text-align: left;
    }
  }

  .contents_title {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 36px;
    font-weight: 700;
    line-height: 48px;

    @media screen and (max-width: 1199px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .contents_description {
    color: ${STYLE_GUIDE.COLOR.black1};
    font-size: 22px;
    font-weight: 400;
    line-height: 34px;

    @media screen and (max-width: 1199px) {
      font-size: 18px;
      line-height: 30px;
    }
  }

  .keyword_chips_list {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    @media screen and (max-width: 1199px) {
      gap: 12px;
    }
  }

  .keyword_chip_item {
    padding: 12px 20px;
    border: 1px solid ${STYLE_GUIDE.COLOR.gray1};
    border-radius: 100px;
    color: ${STYLE_GUIDE.COLOR.black3};
    font-size: 18px;
    font-weight: 400;
    line-height: 30px;

    @media screen and (max-width: 1199px) {
      padding: 8px 19.5px;
      font-size: 16px;
      line-height: 30px;
    }
  }

  .contents_card_list {
    display: flex;
    flex-direction: column;
    gap: 120px;

    @media screen and (max-width: 1199px) {
      gap: 80px;
    }
  }

  .card_divider {
    height: 0;
    border-top: 1px solid ${STYLE_GUIDE.COLOR.gray2};
  }

  .banner {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 150px 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    color: ${STYLE_GUIDE.COLOR.black2};

    @media screen and (max-width: 1919px) {
      padding: 66px 0;
    }

    @media screen and (max-width: 1199px) {
      padding: 105px 0;
    }

    @media screen and (max-width: 883px) {
      padding: 34px 0;
      height: 332px;
    }
  }

  .banner_title {
    font-size: 36px;
    font-weight: 700;
    line-height: 48px;

    @media screen and (max-width: 1199px) {
      font-size: 28px;
      line-height: 40px;
    }

    @media screen and (max-width: 883px) {
      font-size: 24px;
      line-height: 36px;
    }
  }

  .banner_description {
    margin-top: 50px;
    font-size: 22px;
    font-weight: 400;
    line-height: 34px;

    @media screen and (max-width: 1919px) {
      margin-top: 30px;
    }

    @media screen and (max-width: 1199px) {
      margin-top: 20px;
      font-size: 16px;
      line-height: 30px;
    }

    @media screen and (max-width: 883px) {
      font-size: 18px;
    }
  }

  .footer {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 846px;
    color: ${STYLE_GUIDE.COLOR.black3};
    font-size: 32px;
    font-weight: 400;
    line-height: 44px;
    text-align: center;
    background-size: 100% auto;
    background-position: center;
    background-repeat: no-repeat;
    white-space: pre-line;

    @media screen and (max-width: 1919px) {
      height: 528px;
    }

    @media screen and (max-width: 1199px) {
      height: 778px;
      font-size: 16px;
      line-height: 30px;
    }

    @media screen and (max-width: 883px) {
      height: 317px;
    }
  }
`
